import { ParsedRecord } from 'src/components/import/ImportParser';
import {
    BUSINESS_SEGMENT_VALUES,
    EDC_DEP_MAP,
    EDC_VALUES,
    METER_TYPE,
    PRICING_POINT,
    REGISTRATION_LEAD_EXCEPTION,
    REGISTRATION_LEAD_TIME,
    REGISTRATION_MEASUREMENT_METHOD,
    REGISTRATION_RESOURCE_TYPE,
    RESIDENTIAL_PROGRAM_TYPE,
    ZONE,
    ZONE_DEP_MAP,
} from '../values';
import { strikePrice, validateRegistrationDates } from '../validators';
import { calcMaxLoadReductionCapability } from '../calcMaxLoadReductionCapability';
import { PjmLoadReductionType } from '../dto/enums';
import {
    edcAccountNumberHasCorrectFormat,
    locationNameHasOnlyAllowedCharacters,
    stringLengthInRange,
    isNotEmptyStringHandler,
    isNumber,
    isIntegerNumber,
    isOneOfHandler,
    isYesOrNoHandler,
    locationAddressHasOnlyAllowedCharacters,
    locationZipCodeHasOnlyAllowedCharacters,
    numberInRange,
    isDateHandlerWithTimezone,
    REGISTRATION_PROGRAM_FOR_IMPORT,
    REGISTRATION_PRODUCT_TYPE_FOR_IMPORT,
    prettyPrintOptions,
    numberIsPositive,
} from './index';
import { MAX_GENERATORS_AMOUNT } from '..';

export const pjmLocationCsvConfig = [
    {
        column: 'Location*',
        required: true,
        description: 'Name of the location, less than 100 characters',
        width: 250,
    },
    {
        column: 'CSP*',
        required: true,
        description: 'Use ESPON',
        width: 80,
    },
    {
        column: 'EDC Account Number*',
        required: true,
        description: 'Only letters and numbers, other rules depends on selected EDC',
        width: 200,
    },
    {
        column: 'EDC*',
        required: true,
        description: `IS one of ${prettyPrintOptions(EDC_VALUES)}`,
    },
    {
        column: 'Zone*',
        required: true,
        description: `Depends on EDC, is one of ${prettyPrintOptions(ZONE)}`,
    },
    {
        column: 'Pricing Point',
        required: false,
        description: `Depends on Zone, is one of ${prettyPrintOptions(PRICING_POINT)}`,
        width: 150,
    },
    {
        column: 'Business Segment*',
        required: true,
        description: `IS one of ${prettyPrintOptions(BUSINESS_SEGMENT_VALUES)}`,
        width: 200,
    },
    {
        column: 'Allow EDC to submit data (Yes/No)*',
        required: true,
        description: '',
        width: 150,
    },
    {
        column: 'Allow LSE to submit data (Yes/No)*',
        required: true,
        description: '',
        width: 150,
    },
    {
        column: 'Address Line 1*',
        required: true,
        description: '',
        width: 250,
    },
    {
        column: 'Address Line 2',
        required: false,
        description: '',
    },
    {
        column: 'City*',
        required: true,
        description: '',
    },
    {
        column: 'State*',
        required: true,
        description: '2-letter code, CA, NY... Depends on selected EDC',
    },
    {
        column: 'Zip Code*',
        required: true,
        description: '',
    },
    {
        column: 'Utility Residential Participation (Yes/No)*',
        required: true,
        description: '',
        width: 150,
    },
    {
        column: '# Of Participants',
        required: false,
        description: 'Required if Utility Residential Participation is Yes',
        width: 150,
    },
    {
        column: 'Utility Residential Program Type',
        required: false,
        description: 'Required if Utility Residential Participation is Yes',
        width: 150,
    },
    {
        column: 'Meter Type (EDC_METER)*',
        required: true,
        description: 'Currently we support only EDC_METER value',
    },
    {
        column: 'Peak Load Contribution (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Winter Peak Load (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Capacity Loss Factor',
        required: false,
        description: '',
    },
    {
        column: 'Energy Loss Factor',
        required: false,
        description: '',
    },
    {
        column: 'Max Load (kW)*',
        required: true,
        description:
            'The Max Load value must be equal to or greater than the Maximum Load Reduction Capability (Sum of all Reduction Methods: Manufacturing, HVAC, Generator, Batteries, Lighting, Water Heaters, Refrigeration, Plug Load, Computing)',
    },
    {
        column: 'Manufacturing (kW)',
        required: false,
        description: 'This and bellow Reduction Method fields are not required all, but at least one is required!',
    },
    {
        column: 'HVAC (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Generator (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Number of Generators',
        required: false,
        description: '',
    },
    {
        column: 'Batteries (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Lighting (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Water Heaters (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Refrigeration (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Plug Load (kW)',
        required: false,
        description: '',
    },
    {
        column: 'Computing (kW)',
        required: false,
        description: '',
    },

    // todo: registration fields

    {
        column: 'Registration Name*',
        required: true,
        description: '',
        width: 250,
    },
    {
        column: 'Program*',
        required: true,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_PROGRAM_FOR_IMPORT)}`,
        width: 250,
    },
    {
        column: 'Effective Date (MM/DD/YYYY)*',
        required: true,
        description: 'Date in format MM/DD/YYYY, for example: 01/31/2025',
        width: 250,
    },
    {
        column: 'Termination Date (MM/DD/YYYY)*',
        required: true,
        description: 'Date in format MM/DD/YYYY, for example: 01/31/2025',
        width: 250,
    },
    {
        column: 'Allow Modification EDC (Yes/No)*',
        required: true,
        description: '',
        width: 200,
    },
    {
        column: 'Allow Modification LSE (Yes/No)*',
        required: true,
        description: '',
        width: 200,
    },
    {
        column: 'Measurement Method',
        required: false,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_MEASUREMENT_METHOD)}`,
        width: 200,
    },
    {
        column: 'Summer Managed Load (kW)',
        required: false,
        description: '',
        width: 100,
    },
    {
        column: 'Winter Managed Load (kW)',
        required: false,
        description: '',
        width: 100,
    },
    {
        column: 'Resource Type',
        required: false,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_RESOURCE_TYPE)}`,
        width: 200,
    },
    {
        column: 'Product',
        required: false,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_PRODUCT_TYPE_FOR_IMPORT)}`,
        width: 200,
    },
    {
        column: 'RPM DR Resource ID',
        required: false,
        description: 'Please find the correct ID on the DRHUB Reports page.',
        width: 200,
    },
    {
        column: 'Lead Time*',
        required: true,
        description: `Is one of ${prettyPrintOptions(REGISTRATION_LEAD_TIME)}`,
        width: 100,
    },
    {
        column: 'Lead Exception Documentation (Yes/No)',
        required: false,
        description: `Provide if Lead Time is "Short_60" or "Long_120"; Set Yes, if your company has necessary documentation to support the 30 minute notification exception in accordance with PJM tariff and rules`,
        width: 100,
    },
    {
        column: 'Lead Exception Reason',
        required: false,
        description: `Provide if Lead Time is "Short_60" or "Long_120"; Is one of ${prettyPrintOptions(REGISTRATION_LEAD_EXCEPTION)}`,
        width: 100,
    },
    {
        column: 'Lead Exception Description',
        required: false,
        description: `Provide if Lead Time is "Short_60" or "Long_120";`,
        width: 100,
    },
    {
        column: 'Summer Only (Yes/No)*',
        required: true,
        description:
            'Yes - means the user confirms this registration is being used to fulfill a Summer Only RPM or FRR Commitment and is required to test if not required to respond to dispatch in the summer.',
        width: 200,
    },
    {
        column: 'Strike Price (US$)',
        required: false,
        description: '',
        width: 100,
    },
    {
        column: 'Shutdown Cost (US$)',
        required: false,
        description: '',
        width: 100,
    },
    {
        column: 'Residential Registration (Yes/No)',
        required: false,
        description: '',
        width: 200,
    },
    {
        column: 'Residential Program Type',
        required: false,
        description: `Provide only if "Residential Registration (Yes/No)" is "Yes"; Is one of ${prettyPrintOptions(RESIDENTIAL_PROGRAM_TYPE)}`,
        width: 100,
    },
    {
        column: 'Load Data Sample (Yes/No)',
        required: false,
        description: 'Provide only if "Residential Registration (Yes/No)" is "Yes"',
        width: 200,
    },
    {
        column: '# Sample Participants',
        required: false,
        description: 'Provide only if "Residential Registration (Yes/No)" is "Yes"',
        width: 200,
    },
] as const; // tricky - but it needs to properly infer PjmLocationCsvColumns type bellow

export type PjmLocationCsvColumns = (typeof pjmLocationCsvConfig)[number]['column'];

export function pjmLocationParser(row: ParsedRecord<PjmLocationCsvColumns>): ParsedRecord<PjmLocationCsvColumns> {
    /**
     * LOCATION COLUMNS
     */
    const edc: string = row['EDC*'].value;
    const zoneListForSelectedEdc = EDC_DEP_MAP[edc]?.zone || [];
    const stateListForSelectedEdc = EDC_DEP_MAP[edc]?.state || [];

    row = isNotEmptyStringHandler<PjmLocationCsvColumns>(row, 'Location*', [locationNameHasOnlyAllowedCharacters, stringLengthInRange(0, 100)]);
    row = isOneOfHandler<PjmLocationCsvColumns>(row, 'CSP*', ['ESPON']);
    row = isNotEmptyStringHandler<PjmLocationCsvColumns>(row, 'EDC Account Number*', [edcAccountNumberHasCorrectFormat(edc)]);
    row = isOneOfHandler<PjmLocationCsvColumns>(row, 'EDC*', EDC_VALUES);

    row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Zone*', zoneListForSelectedEdc);
    const zone: string = row['Zone*'].parsedValue;
    const pricingPointListForSelectedEdc = ZONE_DEP_MAP[zone]?.pricingPoint || [];

    /** we allow empty value here, but if it is provided - we need to validate */
    if (row['Pricing Point'].value !== '') {
        row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Pricing Point', pricingPointListForSelectedEdc);
    }
    row['Pricing Point'].parsedValue = row['Pricing Point'].value;

    row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Business Segment*', BUSINESS_SEGMENT_VALUES);
    row = isYesOrNoHandler<PjmLocationCsvColumns>(row, 'Allow EDC to submit data (Yes/No)*');
    row = isYesOrNoHandler<PjmLocationCsvColumns>(row, 'Allow LSE to submit data (Yes/No)*');

    row = isNotEmptyStringHandler<PjmLocationCsvColumns>(row, 'Address Line 1*', [
        locationAddressHasOnlyAllowedCharacters,
        stringLengthInRange(0, 80),
    ]);

    if (row['Address Line 2'].value !== '') {
        row = isNotEmptyStringHandler<PjmLocationCsvColumns>(row, 'Address Line 2', [
            locationAddressHasOnlyAllowedCharacters,
            stringLengthInRange(0, 80),
        ]);
    }
    row = isNotEmptyStringHandler<PjmLocationCsvColumns>(row, 'City*', [locationAddressHasOnlyAllowedCharacters, stringLengthInRange(0, 40)]);
    row = isOneOfHandler<PjmLocationCsvColumns>(row, 'State*', stateListForSelectedEdc);
    row = isNotEmptyStringHandler<PjmLocationCsvColumns>(row, 'Zip Code*', [locationZipCodeHasOnlyAllowedCharacters, stringLengthInRange(5, 10)]);

    row = isYesOrNoHandler<PjmLocationCsvColumns>(row, 'Utility Residential Participation (Yes/No)*');

    /** these are required only if Utility Residential Participation (Yes/No)* is Yes */
    if (row['Utility Residential Participation (Yes/No)*'].value === 'Yes') {
        row = isNumber<PjmLocationCsvColumns>(row, '# Of Participants');
        row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Utility Residential Program Type', RESIDENTIAL_PROGRAM_TYPE);
    }

    row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Meter Type (EDC_METER)*', METER_TYPE);

    /** Registration input */
    if (row['Peak Load Contribution (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Peak Load Contribution (kW)', numberInRange(0, 9999999.999));
    }
    if (row['Winter Peak Load (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Winter Peak Load (kW)', numberInRange(0, 9999999.999));
    }
    if (row['Capacity Loss Factor'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Capacity Loss Factor', numberInRange(0, 1.99999));
    }
    if (row['Energy Loss Factor'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Energy Loss Factor', numberInRange(0, 1.99999));
    }
    if (row['Max Load (kW)*'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Max Load (kW)*', numberInRange(0, 9999999.999));
    }
    // row = isNumber<PjmCsvColumns>(row, 'Maximum Load Reduction Capability (kW)*'); // this is calculated from bellow fields (HVAC, Generator...)

    /** Load Reduction Methods */
    if (row['Manufacturing (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Manufacturing (kW)', numberInRange(0, 999999.999));
    }
    if (row['HVAC (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'HVAC (kW)', numberInRange(0, 999999.999));
    }
    if (row['Generator (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Generator (kW)', numberInRange(0, 500000));
    }
    if (row['Number of Generators'].value !== '') {
        row = isIntegerNumber<PjmLocationCsvColumns>(row, 'Number of Generators', numberIsPositive());
    }
    if (row['Lighting (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Lighting (kW)', numberInRange(0, 999999.999));
    }
    if (row['Water Heaters (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Water Heaters (kW)', numberInRange(0, 999999.999));
    }
    if (row['Refrigeration (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Refrigeration (kW)', numberInRange(0, 999999.999));
    }
    if (row['Plug Load (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Plug Load (kW)', numberInRange(0, 999999.999));
    }
    if (row['Computing (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Computing (kW)', numberInRange(0, 999999.999));
    }

    if (row['Number of Generators'].parsedValue === 0 && row['Generator (kW)'].parsedValue > 0) {
        row['Number of Generators'].error = 'When Generator (kW) is provided, this value cannot be zero';
    }
    if (row['Number of Generators'].parsedValue > 0 && row['Generator (kW)'].parsedValue === 0) {
        row['Number of Generators'].error = 'When Generator (kW) is zero, this value must be zero too';
    }
    if (row['Number of Generators'].parsedValue > MAX_GENERATORS_AMOUNT) {
        row['Number of Generators'].error = 'Cannot have more than 10 generators';
    }

    if (row['Batteries (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Batteries (kW)', numberInRange(0, 500000));
    }

    if (
        !row['Manufacturing (kW)'].parsedValue &&
        !row['HVAC (kW)'].parsedValue &&
        !row['Generator (kW)'].parsedValue &&
        !row['Batteries (kW)'].parsedValue &&
        !row['Lighting (kW)'].parsedValue &&
        !row['Water Heaters (kW)'].parsedValue &&
        !row['Refrigeration (kW)'].parsedValue &&
        !row['Plug Load (kW)'].parsedValue &&
        !row['Computing (kW)'].parsedValue
    ) {
        row['Manufacturing (kW)'].error = 'At least one Reduction method must be set';
    }

    /** The Max Load value must be equal to or greater than the Maximum Load Reduction Capability (Sum of Reduction Methods) */
    const loadReduction = calcMaxLoadReductionCapability([
        { reductionType: PjmLoadReductionType.MANUFACTURING, reductionValue: row['Manufacturing (kW)'].parsedValue || 0 },
        { reductionType: PjmLoadReductionType.HVAC, reductionValue: row['HVAC (kW)'].parsedValue || 0 },
        { reductionType: PjmLoadReductionType.GENERATOR, reductionValue: row['Generator (kW)'].parsedValue || 0 },
        { reductionType: PjmLoadReductionType.BATTERIES, reductionValue: row['Batteries (kW)'].parsedValue || 0 },
        { reductionType: PjmLoadReductionType.LIGHTING, reductionValue: row['Lighting (kW)'].parsedValue || 0 },
        { reductionType: PjmLoadReductionType.WATER_HEATERS, reductionValue: row['Water Heaters (kW)'].parsedValue || 0 },
        { reductionType: PjmLoadReductionType.REFRIGERATION, reductionValue: row['Refrigeration (kW)'].parsedValue || 0 },
        { reductionType: PjmLoadReductionType.PLUG_LOAD, reductionValue: row['Plug Load (kW)'].parsedValue || 0 },
        { reductionType: PjmLoadReductionType.COMPUTING, reductionValue: row['Computing (kW)'].parsedValue || 0 },
    ]);
    if (!row['Max Load (kW)*'].parsedValue || row['Max Load (kW)*'].parsedValue < loadReduction) {
        row['Max Load (kW)*'].error =
            'The Max Load value must be equal to or greater than the Maximum Load Reduction Capability (Sum of Reduction Methods)';
    }

    /**
     * REGISTRATION COLUMNS
     */

    // if all registration related columns are empty - no need to validate them
    const isRegistrationAbsent =
        row['Registration Name*'].value.trim() === '' &&
        row['Program*'].value.trim() === '' &&
        row['Effective Date (MM/DD/YYYY)*'].value.trim() === '' &&
        row['Termination Date (MM/DD/YYYY)*'].value.trim() === '' &&
        row['Allow Modification EDC (Yes/No)*'].value.trim() === '' &&
        row['Allow Modification LSE (Yes/No)*'].value.trim() === '' &&
        row['Measurement Method'].value.trim() === '' &&
        row['Summer Managed Load (kW)'].value.trim() === '' &&
        row['Winter Managed Load (kW)'].value.trim() === '' &&
        row['Resource Type'].value.trim() === '' &&
        row['Product'].value.trim() === '' &&
        row['RPM DR Resource ID'].value.trim() === '' &&
        row['Lead Time*'].value.trim() === '' &&
        row['Lead Exception Documentation (Yes/No)'].value.trim() === '' &&
        row['Lead Exception Reason'].value.trim() === '' &&
        row['Lead Exception Description'].value.trim() === '' &&
        row['Strike Price (US$)'].value.trim() === '' &&
        row['Shutdown Cost (US$)'].value.trim() === '' &&
        row['Residential Registration (Yes/No)'].value.trim() === '' &&
        row['Residential Program Type'].value.trim() === '' &&
        row['Load Data Sample (Yes/No)'].value.trim() === '' &&
        row['# Sample Participants'].value.trim() === '';

    if (isRegistrationAbsent) {
        return row;
    }

    /**
     * If there is registration but location does not have Meter data - show error
     */
    if (!row['Meter Type (EDC_METER)*'].parsedValue) {
        row['Meter Type (EDC_METER)*'].error = 'Meter Equipment must be entered before you can register this location!';
    }

    row = isNotEmptyStringHandler<PjmLocationCsvColumns>(row, 'Registration Name*', [
        locationNameHasOnlyAllowedCharacters,
        stringLengthInRange(0, 40),
    ]);
    row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Program*', REGISTRATION_PROGRAM_FOR_IMPORT);
    row = isDateHandlerWithTimezone<PjmLocationCsvColumns>(row, 'Effective Date (MM/DD/YYYY)*', 'start');
    row = isDateHandlerWithTimezone<PjmLocationCsvColumns>(row, 'Termination Date (MM/DD/YYYY)*', 'end');

    if (row['Program*'].parsedValue) {
        const startDate = new Date(row['Effective Date (MM/DD/YYYY)*'].parsedValue);
        const endDate = new Date(row['Termination Date (MM/DD/YYYY)*'].parsedValue);
        const res = validateRegistrationDates(row['Program*'].parsedValue, startDate, endDate);
        if (!res.valid) {
            row['Effective Date (MM/DD/YYYY)*'].error = res.error?.message;
        }
    }

    row = isYesOrNoHandler<PjmLocationCsvColumns>(row, 'Allow Modification EDC (Yes/No)*');
    row = isYesOrNoHandler<PjmLocationCsvColumns>(row, 'Allow Modification LSE (Yes/No)*');

    if (row['Measurement Method'].value !== '') {
        row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Measurement Method', REGISTRATION_MEASUREMENT_METHOD);
    }

    if (row['Summer Managed Load (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Summer Managed Load (kW)');
    }

    if (row['Winter Managed Load (kW)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Winter Managed Load (kW)');
    }

    if (row['Resource Type'].value !== '') {
        row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Resource Type', REGISTRATION_RESOURCE_TYPE);
    }

    if (row['Product'].value !== '') {
        row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Product', REGISTRATION_PRODUCT_TYPE_FOR_IMPORT);
    }

    // RPM DR Resource - optional
    // ! DRHUB expects number (ID) here but we don't know how to get this number (by API)
    // ! so we relay on a user - they can visit DRHUB Reports page, and find correct ID by RPM DR Resource name
    // ! and place the ID here (in the imported file)
    if (row['RPM DR Resource ID'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'RPM DR Resource ID');
    }

    row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Lead Time*', REGISTRATION_LEAD_TIME);

    if (row['Lead Exception Documentation (Yes/No)'].value !== '') {
        row = isYesOrNoHandler<PjmLocationCsvColumns>(row, 'Lead Exception Documentation (Yes/No)');
    }

    if (row['Lead Exception Reason'].value !== '') {
        row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Lead Exception Reason', REGISTRATION_LEAD_EXCEPTION);
    }
    row['Lead Exception Description'].parsedValue = row['Lead Exception Description'].value;

    row = isYesOrNoHandler<PjmLocationCsvColumns>(row, 'Summer Only (Yes/No)*');

    if (row['Strike Price (US$)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Strike Price (US$)');
    }

    if (row['Shutdown Cost (US$)'].value !== '') {
        row = isNumber<PjmLocationCsvColumns>(row, 'Shutdown Cost (US$)');
    }

    if (row['Strike Price (US$)'].parsedValue && row['Lead Time*'].parsedValue) {
        const result = strikePrice.isValid(row['Strike Price (US$)'].parsedValue, row['Lead Time*'].parsedValue);
        if (!result.valid) {
            row['Strike Price (US$)'].error = result.error.message;
        }
    }

    if (row['Residential Registration (Yes/No)'].value) {
        row = isYesOrNoHandler<PjmLocationCsvColumns>(row, 'Residential Registration (Yes/No)');
    }

    if (row['Residential Registration (Yes/No)'].parsedValue === true) {
        if (row['Residential Program Type'].value) {
            row = isOneOfHandler<PjmLocationCsvColumns>(row, 'Residential Program Type', RESIDENTIAL_PROGRAM_TYPE);
        }

        if (row['Load Data Sample (Yes/No)'].value) {
            row = isYesOrNoHandler<PjmLocationCsvColumns>(row, 'Load Data Sample (Yes/No)');
        }

        if (row['# Sample Participants'].value) {
            row = isNumber<PjmLocationCsvColumns>(row, '# Sample Participants');
        }
    }

    return row;
}
